import React from 'react';
import {GoogleService, WeatherService} from '../api';
import {PaginatedResult, WeatherStationFromList} from '../api/types';
import {MyWeather} from '../utils';
import type {
  PlaceResults,
  SearchSourceName,
  StationResults,
} from '../utils/MyWeather/search';
import useApiClient, {
  DebounceSupersededError,
  isThrottled,
} from './useApiClient';

export type {
  PlaceResults,
  SearchSourceName,
  SearchStatus,
  StationResults,
} from '../utils/MyWeather/search';

type Position = {latitude: number; longitude: number};

type StationFilter =
  | ReturnType<typeof MyWeather.stationSearchFilter>
  | ReturnType<typeof MyWeather.stationNearbyFilter>;

const IDLE_PLACES: PlaceResults = {status: 'idle', items: []};
const IDLE_STATIONS: StationResults = {
  status: 'idle',
  items: [],
  count: 0,
  hasMore: false,
  loadingMore: false,
  moreFailed: false,
};
// A failed source shows nothing it found before: no stale row, count or
// spoken summary. Refused for too many requests (a 429, from the server or
// held back by the client), it says so (FE #427).
const failedPlaces = (error: unknown): PlaceResults =>
  isThrottled(error)
    ? {status: 'error', items: [], throttled: true}
    : {status: 'error', items: []};
const failedStations = (error: unknown): StationResults =>
  isThrottled(error)
    ? {...IDLE_STATIONS, status: 'error', throttled: true}
    : {...IDLE_STATIONS, status: 'error'};

// Typing: wait for a pause. Silent: a failure is shown in its section,
// never as the app's error snackbar.
const TYPING = {debounce: 500, silent: true};
const NOW = {silent: true};

/**
 * My weather's one search (FE #381 step 2). From one field it asks Google
 * for places and the API for stations by name (nearest first when
 * `position` is known), in parallel.
 *
 * - Each source has its own status, so one failing or slow never hides the
 *   other; `retry` asks one again.
 * - Under `MyWeather.SEARCH_MIN_LENGTH` letters nothing is asked and both
 *   are idle.
 * - An answer to anything but the latest request is dropped, and while a
 *   new query loads the last results stay, with the status `loading`; a
 *   failed source shows nothing it found before.
 * - The station list is paged: `loadMoreStations` asks for the next page,
 *   with the first page's filter, and lists each station once; never while
 *   a first page loads. A failed page sets `moreFailed`, and only
 *   `retryMoreStations` asks it again: the list's end-reached event fires
 *   again as soon as a footer changes its length, so asking by itself
 *   would loop while offline.
 * - `searchNearby` lists the stations nearest a position instead, whatever
 *   their name, and no places (the field's ⌖). Typing or `clear` ends it.
 */
const useMyWeatherSearch = (position: Position | null) => {
  const {fetch} = useApiClient();
  const [query, setQuery] = React.useState<string | null>(null);
  const [places, setPlaces] = React.useState<PlaceResults>(IDLE_PLACES);
  const [stations, setStations] = React.useState<StationResults>(IDLE_STATIONS);
  const [nearby, setNearby] = React.useState(false);
  // The position the nearby list is around, for a retry; null otherwise.
  const nearbyPosition = React.useRef<Position | null>(null);

  // The query being searched.
  const current = React.useRef<string | null>(null);
  // One generation per source, bumped by every new request for it (and by
  // clear): an answer belongs to the generation it was asked in, never to
  // its text, which the user may type again.
  const placesGen = React.useRef(0);
  const stationsGen = React.useRef(0);
  // The station filter of the current query, fixed at its first page so
  // every page is sorted alike; the last page asked for; one in flight.
  const stationFilter = React.useRef<StationFilter | null>(null);
  const stationPage = React.useRef(1);
  const loadingMore = React.useRef(false);
  // Set from the moment a first page is asked until it answers: read when
  // a further page is asked, since the list may call an end-reached
  // handler from before the re-render (its `stations.status` still ready).
  const firstPageLoading = React.useRef(false);
  const moreFailed = React.useRef(false);
  // Whether the list has a further page, read alike: a handler from an
  // earlier render would ask past the last page.
  const hasMore = React.useRef(false);
  // The position is read when asking, not a reason to ask again.
  const positionRef = React.useRef(position);
  React.useEffect(() => {
    positionRef.current = position;
  }, [position]);

  const askPlaces = React.useCallback(
    (q: string, options: typeof TYPING | typeof NOW) => {
      const gen = ++placesGen.current;
      // A refusal is the failure's: not carried into what loads now.
      setPlaces(({items}) => ({status: 'loading', items}));
      fetch(GoogleService.autocomplete(q), options)
        .then((items) => {
          if (placesGen.current === gen) {
            setPlaces({status: 'ready', items: items ?? []});
          }
        })
        .catch((error) => {
          if (
            !(error instanceof DebounceSupersededError) &&
            placesGen.current === gen
          ) {
            setPlaces(failedPlaces(error));
          }
        });
    },
    [fetch],
  );

  const askStations = React.useCallback(
    (filter: StationFilter, options: typeof TYPING | typeof NOW) => {
      const gen = ++stationsGen.current;
      stationFilter.current = filter;
      stationPage.current = 1;
      loadingMore.current = false;
      firstPageLoading.current = true;
      moreFailed.current = false;
      hasMore.current = false;
      setStations(({throttled: _refused, ...previous}) => ({
        ...previous,
        status: 'loading',
        loadingMore: false,
        moreFailed: false,
      }));
      fetch(WeatherService.stationList(filter), options)
        .then((page: PaginatedResult<WeatherStationFromList[]>) => {
          if (stationsGen.current === gen) {
            firstPageLoading.current = false;
            hasMore.current = page.next != null;
            setStations({
              status: 'ready',
              items: page.results,
              count: page.count,
              hasMore: page.next != null,
              loadingMore: false,
              moreFailed: false,
            });
          }
        })
        .catch((error) => {
          if (
            !(error instanceof DebounceSupersededError) &&
            stationsGen.current === gen
          ) {
            firstPageLoading.current = false;
            setStations(failedStations(error));
          }
        });
    },
    [fetch],
  );

  const clear = React.useCallback(() => {
    current.current = null;
    nearbyPosition.current = null;
    placesGen.current += 1;
    stationsGen.current += 1;
    loadingMore.current = false;
    firstPageLoading.current = false;
    moreFailed.current = false;
    hasMore.current = false;
    setNearby(false);
    setQuery(null);
    setPlaces(IDLE_PLACES);
    setStations(IDLE_STATIONS);
  }, []);

  const search = React.useCallback(
    (text: string) => {
      const q = MyWeather.searchQuery(text);
      if (!q) {
        clear();
        return;
      }
      if (q === current.current) {
        return; // a space added or removed: the same search
      }
      current.current = q;
      nearbyPosition.current = null;
      setNearby(false);
      setQuery(q);
      askPlaces(q, TYPING);
      askStations(
        MyWeather.stationSearchFilter(q, positionRef.current),
        TYPING,
      );
    },
    [askPlaces, askStations, clear],
  );

  const searchNearby = React.useCallback(
    (around: Position) => {
      current.current = null;
      nearbyPosition.current = around;
      // No places here: an answer to a name search still out is dropped.
      placesGen.current += 1;
      setQuery(null);
      setPlaces(IDLE_PLACES);
      setNearby(true);
      // Its own list from the start: never the replaced search's rows, nor
      // their "more", under the nearby header.
      setStations(IDLE_STATIONS);
      askStations(MyWeather.stationNearbyFilter(around), NOW);
    },
    [askStations],
  );

  const retry = React.useCallback(
    (source: SearchSourceName) => {
      const around = nearbyPosition.current;
      if (around) {
        if (source === 'stations') {
          askStations(MyWeather.stationNearbyFilter(around), NOW);
        }
        return;
      }
      const q = current.current;
      if (!q) {
        return;
      }
      if (source === 'places') {
        askPlaces(q, NOW);
      } else {
        askStations(MyWeather.stationSearchFilter(q, positionRef.current), NOW);
      }
    },
    [askPlaces, askStations],
  );

  /** The page after the last one listed, with the first page's filter. */
  const askNextPage = React.useCallback(() => {
    const filter = stationFilter.current;
    if (!filter) {
      return;
    }
    const gen = stationsGen.current;
    const next = stationPage.current + 1;
    loadingMore.current = true;
    moreFailed.current = false;
    setStations((previous) => ({
      ...previous,
      loadingMore: true,
      moreFailed: false,
    }));
    fetch(WeatherService.stationList({...filter, page: next}), NOW)
      .then((page: PaginatedResult<WeatherStationFromList[]>) => {
        if (stationsGen.current !== gen) {
          return;
        }
        stationPage.current = next;
        hasMore.current = page.next != null;
        setStations((previous) => {
          // Pages can overlap when stations tie on distance.
          const seen = new Set(previous.items.map((station) => station.id));
          return {
            ...previous,
            items: [
              ...previous.items,
              ...page.results.filter((station) => !seen.has(station.id)),
            ],
            count: page.count,
            hasMore: page.next != null,
            loadingMore: false,
            moreFailed: false,
          };
        });
      })
      .catch(() => {
        if (stationsGen.current === gen) {
          moreFailed.current = true;
          setStations((previous) => ({
            ...previous,
            loadingMore: false,
            moreFailed: true,
          }));
        }
      })
      .finally(() => {
        if (stationsGen.current === gen) {
          loadingMore.current = false;
        }
      });
  }, [fetch]);

  const loadMoreStations = React.useCallback(() => {
    // Not while a first page loads: the rows shown (and their hasMore) are
    // the last query's, and its page 1 would replace the page asked. Not
    // after a failed page either: that waits for retryMoreStations.
    if (
      stations.status !== 'ready' ||
      !hasMore.current ||
      firstPageLoading.current ||
      loadingMore.current ||
      moreFailed.current
    ) {
      return;
    }
    askNextPage();
  }, [askNextPage, stations.status]);

  /** Try again: the page that failed, the one after the last listed. */
  const retryMoreStations = React.useCallback(() => {
    if (!moreFailed.current || loadingMore.current) {
      return;
    }
    askNextPage();
  }, [askNextPage]);

  return {
    query,
    nearby,
    places,
    stations,
    search,
    searchNearby,
    retry,
    loadMoreStations,
    retryMoreStations,
    clear,
  };
};

export default useMyWeatherSearch;
